import React from "react";
import Highcharts, { dateFormat } from "highcharts";
import { DateTime } from "luxon";

import { Box } from "@material-ui/core";

import StockLine from "components/visuals/StockLine";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppDispatch, useAppSelector } from "store";
import { CostGranularity } from "modules/customer/insights/cost/costGranularity";
import {
    selectHasErrors,
    selectIsLoading,
    selectCostMeasureType,
    selectTotalCostTrendsOverTime,
    setCostOverTimeGranularity,
    selectCostOverTimeGranularity
} from "modules/customer/insights/cost/costOverview/costOverviewSlice";
import { CostMeasureType } from "modules/customer/insights/cost/costMeasureType";

import numberFormatter from "utils/numberFormatter";
import stringUtils from "utils/stringUtils";
import { abs, isString } from "mathjs";
import { selectUseCustomCalendar } from "modules/customer/insights/cost/costSlice";

const TotalCostTrendsOverTime: React.FC = () => {
    const dispatch = useAppDispatch();
    const colourPalette = useColourPalette();
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const costMeasureType = useAppSelector(selectCostMeasureType);
    const totalCostsOverTime = useAppSelector(selectTotalCostTrendsOverTime);
    const chartGranularity = useAppSelector(selectCostOverTimeGranularity);
    const useCustomCalendar = useAppSelector(selectUseCustomCalendar);

    const series: Highcharts.SeriesOptionsType[] = [];

    const seriesKeys = Object.keys(totalCostsOverTime);
    const seriesData = seriesKeys.length > 0 ? totalCostsOverTime[seriesKeys[0]] : [];

    const selectedButton = chartGranularity === CostGranularity.Month
        ? 0
        : chartGranularity === CostGranularity.Quarter ? 1 : 2;

    const getCustomLabel = (timestamp: string | number, granularity: CostGranularity) => {
        if (isString(timestamp) || seriesData.length === 0) {
            return String(timestamp);
        }
        // Find closest date in dates array
        let difference = Number.MAX_VALUE;
        let dateIndex = 0;
        seriesData.forEach((item, index) => {
            const newDifference = abs(timestamp - item.date.toMillis());
            if (newDifference < difference) {
                difference = newDifference;
                dateIndex = index;
            }
        });
        if (granularity === CostGranularity.Month) {
            return seriesData[dateIndex].customDate.dateLabel;
        }
        if (granularity === CostGranularity.Quarter) {
            return seriesData[dateIndex].customDate.quarterLabel;
        }
        if (granularity === CostGranularity.Year) {
            return String(seriesData[dateIndex].customDate.financialYearLabel);
        }
        return seriesData[dateIndex].customDate.dateLabel;
    };

    //ToDo: Use separate date array containing all period dates
    const tickPositions = seriesData.map(item => item.customDate.date.toMillis());

    const navigatorTickPositions = chartGranularity === CostGranularity.Month
        ? [...seriesData.filter(item => item.customDate.periodNumber === 7).map(item => item.customDate.date.toMillis())]
        : chartGranularity === CostGranularity.Quarter
            ? [...seriesData.filter(item => item.customDate.quarterNumber === 3).map(item => item.customDate.date.toMillis())]
            : undefined;

    Object.keys(totalCostsOverTime).forEach(key => {
        series.push({
            type: "spline",
            showInNavigator: true,
            name: key,
            data: totalCostsOverTime[key].map(cost =>
                [DateTime.fromISO(cost.date.toISODate(), { zone: "utc" }).toMillis(), (costMeasureType === CostMeasureType.CostValue ? cost.costValue : cost.costPercentageOfRevenue)]
            ),
            color: colourPalette.categorical[Object.keys(totalCostsOverTime).indexOf(key)]
        });
    });

    const options: Highcharts.Options = {
        xAxis: {
            type: 'datetime',
            ...(useCustomCalendar && {
                labels: {
                    formatter: function () {
                        return getCustomLabel(this.value, chartGranularity);
                    }
                },
                tickPositions
            })
        },
        yAxis: {
            title: {
                text: (costMeasureType === CostMeasureType.CostValue ? "Costs" : "Cost as a % of revenue")
            },
            labels: {
                formatter: function () {
                    return (
                        costMeasureType === CostMeasureType.CostValue ?
                            numberFormatter.toGBP(this.value, 0) :
                            numberFormatter.toPercentage(this.value, true, 0)
                    );
                }
            }
        },
        legend: {
            enabled: true
        },
        series,
        navigator: {
            series: {
                fillColor: 'none',
                fillOpacity: 0
            },
            ...(useCustomCalendar && {
                xAxis: {
                    showFirstLabel: true,
                    labels: {
                        formatter: function () {
                            return getCustomLabel(this.value, CostGranularity.Year);
                        }
                    },
                    tickPositions: navigatorTickPositions
                }
            })
        },
        rangeSelector: {
            allButtonsEnabled: true,
            selected: selectedButton,
            buttons: [{
                type: "month",
                count: 12,
                text: useCustomCalendar ? "Period" : "Month",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostOverTimeGranularity(CostGranularity.Month));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "month",
                count: 24,
                text: "Quarter",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostOverTimeGranularity(CostGranularity.Quarter));
                    }
                },
                preserveDataGrouping: true
            }, {
                type: "year",
                count: 3,
                text: useCustomCalendar ? "FY" : "Year",
                dataGrouping: {
                    enabled: false
                },
                events: {
                    click: function () {
                        dispatch(setCostOverTimeGranularity(CostGranularity.Year));
                    }
                },
                preserveDataGrouping: true
            }]
        },
        tooltip: {
            enabled: true,
            shared: false,
            split: false,
            headerFormat: ``,
            footerFormat: ``,
            formatter: function () {
                let header = "";

                if (useCustomCalendar) {
                    header = getCustomLabel(this.point.x, chartGranularity);
                }
                else if (chartGranularity === CostGranularity.Year) {
                    header = dateFormat("%Y", this.point.x);
                }
                else if (chartGranularity === CostGranularity.Month) {
                    header = dateFormat("%B %Y", this.point.x);
                }
                else {
                    const month = DateTime.fromMillis(this.point.x).month;
                    const quarter = Math.ceil(month / 3);
                    header = `Q${quarter} ` + dateFormat("%Y", this.point.x);
                }

                const categoriesArr: string[] = [];
                const categoriesFormatArr: string[] = [];
                const valuesArr: string[] = [];

                costMeasureType === CostMeasureType.CostValue ? categoriesArr.push(this.point.series.name, "Cost value", "Cost as % revenue") : categoriesArr.push(this.point.series.name, "Cost as % revenue", "Cost value");
                categoriesFormatArr.push(`color:${this.point.series.color};font-weight:bold`, "", "");

                const pointValues = totalCostsOverTime[this.point.series.name].find(item => item.date.toMillis() === this.point.x);

                const costValue = numberFormatter.toGBP(pointValues?.costValue ?? 0);
                const percentageValue = numberFormatter.toPercentage(pointValues?.costPercentageOfRevenue ?? 0, true, 1);
                costMeasureType === CostMeasureType.CostValue ? valuesArr.push("", costValue, percentageValue) : valuesArr.push("", percentageValue, costValue);

                return `<table>${stringUtils.tooltipHTML(
                    categoriesArr,
                    {
                        header: header,
                        values: valuesArr,
                        categoryFormattingArr: categoriesFormatArr
                    }
                )}</table>`;
            },
            useHTML: true
        }
    };

    return (
        <Box data-cy="total-cost-trends-over-time">
            <StockLine loading={isLoading} error={hasErrors} options={options} globalOptions={{}} />
        </Box>
    );
};

export default TotalCostTrendsOverTime;
